import { prefetchQuery, usePrefetchableQuery } from '@change-corgi/core/react/async';
import type { PrefetchableQueryHookResult, PrefetchedQueryState } from '@change-corgi/core/react/async';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PrefetchContext } from 'src/shared/prefetch';

import type { MovementInfo } from 'src/app/pages/movement/shared/api';
import { getMovementInfo } from 'src/app/pages/movement/shared/api';

type MovementInfoErrorReason = 'notFound' | 'other';
export type MovementInfoPrefetchedState = PrefetchedQueryState<
	Readonly<{ data: MovementInfo }>,
	Readonly<{ reason: MovementInfoErrorReason }>
>;
export type MovementInfoState = PrefetchableQueryHookResult<
	Readonly<{ data: MovementInfo }>,
	Readonly<{ reason: MovementInfoErrorReason }>
>;

async function getMovementInfoOrThrow(movementId: string, utilityContext: UtilityContext): Promise<MovementInfo> {
	let data: MovementInfo | undefined;
	try {
		data = await getMovementInfo(movementId, utilityContext);
	} catch (e) {
		throw new Error('other' as MovementInfoErrorReason);
	}
	if (!data) {
		throw new Error('notFound' as MovementInfoErrorReason);
	}
	return data;
}

export function useMovementInfo(movementId: string, prefetchedState?: MovementInfoPrefetchedState): MovementInfoState {
	const utilityContext = useUtilityContext();

	return usePrefetchableQuery(
		async () => ({ data: await getMovementInfoOrThrow(movementId, utilityContext) }),
		[movementId, utilityContext],
		{
			id: movementId,
			prefetchedState,
			errorHandler: (e: Error) => ({ reason: e.message as MovementInfoErrorReason }),
		},
	);
}

export async function prefetchMovementInfo(
	movementId: string,
	{ utilityContext }: PrefetchContext,
): Promise<MovementInfoPrefetchedState> {
	return prefetchQuery(async () => ({ data: await getMovementInfoOrThrow(movementId, utilityContext) }), {
		id: movementId,
		errorHandler: (e: Error) => ({ reason: e.message as MovementInfoErrorReason }),
	});
}
